import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from "@mui/material";
import { DateTime } from "luxon";
import DatePickerInput from "components/DatePickerInput";
import { ErrorAlert } from "components/alert/ErrorAlert";

type ReactivateSubscriptionDialogProps = {
  open: boolean;
  subscriptionId: string;
  subscriptionName: string;
  handleClose: () => void;
  submitReactivate: (newTermStart: string, newTermEnd: string | null) => Promise<void>;
};

export function ReactivateSubscriptionDialog(props: ReactivateSubscriptionDialogProps) {
  const [newTermStart, setNewTermStart] = useState<DateTime | null>(DateTime.utc().startOf("day"));
  const [newTermEnd, setNewTermEnd] = useState<DateTime | null>(DateTime.utc().startOf("day").plus({ years: 1 }));
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown | undefined>();

  const handleSubmit = async () => {
    if (!newTermStart || !newTermEnd) {
      setError(new Error("Both term start and end dates are required"));
      return;
    }

    if (newTermStart > DateTime.utc().startOf("day")) {
      setError(new Error("Term start date cannot be in the future"));
      return;
    }

    if (newTermEnd <= newTermStart) {
      setError(new Error("Term end date must be after term start date"));
      return;
    }

    if (newTermEnd < DateTime.utc().startOf("day")) {
      setError(new Error("Term end date cannot be in the past"));
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      await props.submitReactivate(newTermStart.toISODate() || "", newTermEnd.toISODate() || "");
      props.handleClose();
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setNewTermStart(DateTime.utc().startOf("day"));
    setNewTermEnd(DateTime.utc().startOf("day").plus({ years: 1 }));
    setError(undefined);
    props.handleClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        if (!loading) handleCancel();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Reactivate Subscription</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {error ? <ErrorAlert error={error} /> : null}
          <Grid size={12}>
            <DialogContentText>
              Reactivate the subscription <strong>{props.subscriptionName}</strong> by setting new term dates.
            </DialogContentText>
          </Grid>
          <Grid size={12}>
            <DatePickerInput
              label="New Term Start Date"
              value={newTermStart ?? undefined}
              onChange={(date) => setNewTermStart(date)}
              disabled={loading}
              maxDate={DateTime.utc().startOf("day")}
              required={true}
              isInvalid={!!error && (!newTermStart || (newTermStart && newTermStart > DateTime.utc().startOf("day")))}
              showLocalConversion={false}
            />
          </Grid>
          <Grid size={12}>
            <DatePickerInput
              label="New Term End Date"
              value={newTermEnd ?? undefined}
              onChange={(date) => setNewTermEnd(date)}
              disabled={loading}
              minDate={DateTime.utc().startOf("day")}
              required={true}
              isInvalid={
                !!error &&
                (!newTermEnd ||
                  (newTermStart && newTermEnd && newTermEnd <= newTermStart) ||
                  (newTermEnd && newTermEnd < DateTime.utc().startOf("day")))
              }
              showLocalConversion={false}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !newTermStart || !newTermEnd}
        >
          {loading ? "Reactivating..." : "Reactivate Subscription"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
